/**
 * Gece koşusu veri tipleri ve ekranda kullanılan Türkçe etiket / renk eşlemeleri.
 *
 * Sözleşme: icrapro-laravel-api  admin/gece-kosusu uçları
 * Taban yol: {icwApiUrl}admin/gece-kosusu
 */

/**
 * Sunucu saat diliminde gelen ISO-8601 damgasını yerelleştirir.
 *
 * BİLEREK bu dosyada tanımlı: master hattında aynı işlev TenantPoolEntity.ts'de
 * duruyor, ama o dosya test-2 hattında (tenant havuzu ekranı olmadığı için) yok.
 * Oradan içe aktarmak, bu ekranın test2admin derlemesini "module not found" ile
 * düşürüyordu. Davranış TenantPoolEntity.formatDateTime ile birebir aynıdır.
 */
export function formatDateTime(value: string | null | undefined): string {
    if (!value) {
        return "-";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("tr-TR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
}

export type KosuDurum =
    "beklemede" | "calisiyor" | "durduruluyor" | "durduruldu" | "tamamlandi" | "hata" | "kesildi" | "reddedildi";

export type TenantDurum =
    "sirada" | "isleniyor" | "bitti" | "hata" | "atlandi" | "kesildi";

export type HataTipi =
    "timeout" | "read_only" | "deadlock" | "lock_timeout" | "tenant_genel" | "genel"
    /* veri eksik: hesaplayıcının bilerek reddettiği takip — kod hatası değil */
    | "alacak_yok" | "takip_tarihi_yok" | "veri_eksik";

export type KosuTetikleyenTipi = "cron" | "manuel";

export type KosuKapsam = "tum" | "tenant" | "takip";

export type TenantAtlanmaNedeni = "lisans_yok" | "db_yok";

export type TahminGuveni = "kaba" | "orta" | "iyi";

/** GET /admin/gece-kosusu listesindeki her satır. */
export interface KosuOzet {
    id: number;
    hesap_tarihi: string; /* "YYYY-MM-DD" */
    tetikleyen_tip: KosuTetikleyenTipi;
    tetikleyen_ad: string | null;
    durum: KosuDurum;
    kapsam: KosuKapsam;
    kapsam_detay: string | null;
    toplam_tenant: number;
    tamamlanan_tenant: number;
    hatali_tenant: number;
    atlanan_tenant: number;
    baslangic_at: string | null; /* ISO */
    bitis_at: string | null;
    son_nabiz_at: string | null;
    gecen_saniye: number | null;
    /* Durdur/Başlat + otomatik devam + ileri tarihli test alanları. Eski API'lerde yoktur. */
    durdurma_istendi_at?: string | null; /* ISO */
    otomatik_devam_sayisi?: number;
    son_kesinti_at?: string | null; /* ISO */
    ileri_tarihli_test_mi?: boolean;
}

/** GET /admin/gece-kosusu/{kosuId} yanıtının data alanı. */
export interface KosuDetay {
    kosu: KosuOzet & {
        tetikleyen_user_id: number | null;
        klasik_mi: boolean;
        created_at: string;
        updated_at: string;
    };
    ozet: {
        toplam_takip: number;
        gorulen_takip: number;
        hesaplanan_takip: number;
        hesaplanamayan_takip?: number;
    };
    tahmin: {
        tahmini_bitis_at: string | null;
        tahmini_kalan_saniye: number | null;
        tahmin_guveni: TahminGuveni | null;
    };
    tenantlar: KosuTenantSatiri[];
    /** Eski API'lerde yoktur — Durdur/Başlat ve tenant arama düğmeleri bunsuz gizlenir. */
    yetenekler?: KosuYetenekleri;
}

/** KosuDetay.tenantlar[] içindeki her satır. */
export interface KosuTenantSatiri {
    id: number; /* gece_kosu_tenantlari.id — hata ucunda kosuTenantId olarak kullanılır */
    tenant_id: string; /* uuid */
    tenant_adi: string | null; /* BOŞ OLABİLİR */
    durum: TenantDurum;
    atlanma_nedeni: TenantAtlanmaNedeni | null;
    hesap_tarihi: string | null;
    toplam_takip: number;
    gorulen_takip: number;
    hesaplanan_takip: number;
    atlanan_takip: number;
    hatali_takip: number;
    hesaplanamayan_takip?: number; /* veri eksik (hesaplanamaz) takip; hata sayılmaz. Eski API'de yok */
    hata_sayisi: number;
    delta_sayisi: number;
    soguk_sayisi: number;
    atla_sayisi: number;
    toplam_sure_ms: number;
    baslangic_at: string | null;
    bitis_at: string | null;
    son_nabiz_at: string | null;
}

/** GET /admin/gece-kosusu/{kosuId}/tenant/{kosuTenantId}/hatalar sayfalayıcısındaki her satır. */
export interface KosuHataSatiri {
    takip_id: number | null;
    hata_tipi: HataTipi;
    hata_sinifi: string | null;
    hata_mesaji: string | null;
    dosya: string | null;
    satir: number | null;
    created_at: string | null;
}

/* ------------------------------------------------------------------ */
/* Görsel sözleşme                                                      */
/* ------------------------------------------------------------------ */

export interface DurumBilgisi {
    etiket: string;
    renk: string;
    ikon: string;
}

const UNKNOWN_DURUM: DurumBilgisi = {etiket: "Bilinmiyor", renk: "grey", ikon: "mdi-help"};

const KOSU_DURUM_META: { [key: string]: DurumBilgisi } = {
    beklemede: {etiket: "Başlatılıyor", renk: "grey", ikon: "mdi-clock-outline"},
    calisiyor: {etiket: "Çalışıyor", renk: "info", ikon: "mdi-progress-clock"},
    durduruluyor: {etiket: "Durduruluyor", renk: "orange", ikon: "mdi-pause-circle-outline"},
    durduruldu: {etiket: "Durduruldu", renk: "orange darken-3", ikon: "mdi-pause-circle"},
    tamamlandi: {etiket: "Tamamlandı", renk: "success", ikon: "mdi-check-circle"},
    hata: {etiket: "Hata", renk: "error", ikon: "mdi-alert-octagon"},
    kesildi: {etiket: "Kesildi", renk: "warning", ikon: "mdi-stop-circle"},
    reddedildi: {etiket: "Reddedildi", renk: "warning", ikon: "mdi-cancel"}
};

const TENANT_DURUM_META: { [key: string]: DurumBilgisi } = {
    sirada: {etiket: "Sırada", renk: "grey", ikon: "mdi-clock-outline"},
    isleniyor: {etiket: "İşleniyor", renk: "info", ikon: "mdi-progress-clock"},
    bitti: {etiket: "Bitti", renk: "success", ikon: "mdi-check-circle"},
    hata: {etiket: "Hata", renk: "error", ikon: "mdi-alert-octagon"},
    atlandi: {etiket: "Atlandı", renk: "grey", ikon: "mdi-debug-step-over"},
    kesildi: {etiket: "Kesildi", renk: "warning", ikon: "mdi-stop-circle"}
};

const HATA_TIPI_ETIKET: { [key: string]: string } = {
    timeout: "Zaman aşımı",
    read_only: "Salt okunur veritabanı",
    deadlock: "Ölü kilit",
    lock_timeout: "Kilit zaman aşımı",
    tenant_genel: "Büro geneli",
    genel: "Beklenmeyen hata",
    alacak_yok: "Veri eksik: alacak yok",
    takip_tarihi_yok: "Veri eksik: takip tarihi yok",
    veri_eksik: "Veri eksik"
};

/** Veri eksik türleri hata sayılmaz; panelde ayrı ve sakin gösterilir. */
export function veriEksikTipiMi(t: string): boolean {
    return t === "alacak_yok" || t === "takip_tarihi_yok" || t === "veri_eksik";
}

export function kosuDurumBilgisi(d: KosuDurum): DurumBilgisi {
    return KOSU_DURUM_META[d] || {...UNKNOWN_DURUM, etiket: d};
}

export function tenantDurumBilgisi(d: TenantDurum): DurumBilgisi {
    return TENANT_DURUM_META[d] || {...UNKNOWN_DURUM, etiket: d};
}

export function hataTipiEtiketi(t: HataTipi): string {
    return HATA_TIPI_ETIKET[t] || t;
}

/** tenant_adi boş/null ise ekranda kimliksiz kalmasın diye tenant_id'nin ilk 8 karakteri dönülür. */
export function tenantGoruntuAdi(s: KosuTenantSatiri): string {
    if (s.tenant_adi && s.tenant_adi.trim() !== "") {
        return s.tenant_adi;
    }
    return s.tenant_id ? s.tenant_id.slice(0, 8) : "—";
}

/** İlerleme çubukları için yüzde hesabı. payda 0 ise 0 döner (bölme hatası olmaz). */
export function yuzde(pay: number, payda: number): number {
    if (!payda) {
        return 0;
    }
    return Math.round((pay / payda) * 100);
}

/** Saniyeyi kullanıcıya okunur süreye çevirir: "45 sn" / "12 dk" / "2 sa 15 dk". */
export function sureMetni(saniye: number | null): string {
    if (saniye === null || saniye === undefined || isNaN(saniye)) {
        return "—";
    }
    const s = Math.max(0, Math.floor(saniye));
    if (s < 60) {
        return s + " sn";
    }
    if (s < 3600) {
        return Math.floor(s / 60) + " dk";
    }
    const saat = Math.floor(s / 3600);
    const dk = Math.floor((s % 3600) / 60);
    return saat + " sa " + dk + " dk";
}

/**
 * Yalnızca saat:dakika döner ("18:40"); hedef tarih bugün değilse başına
 * "GG.AA " eklenir (tam tarih hem uzun kaçar hem de burada gereksiz —
 * formatDateTime saniye + yıl da taşır, ekranda mutlak bitiş saati için
 * o kadarı fazla). Değer yoksa/parse edilemiyorsa boş döner.
 */
function saatMetni(value: string | null | undefined): string {
    if (!value) {
        return "";
    }
    const tarih = new Date(value);
    if (isNaN(tarih.getTime())) {
        return "";
    }
    const simdi = new Date();
    const bugunMu = tarih.getFullYear() === simdi.getFullYear()
        && tarih.getMonth() === simdi.getMonth()
        && tarih.getDate() === simdi.getDate();
    const saat = tarih.toLocaleTimeString("tr-TR", {hour: "2-digit", minute: "2-digit"});
    if (bugunMu) {
        return saat;
    }
    const gun = tarih.toLocaleDateString("tr-TR", {day: "2-digit", month: "2-digit"});
    return gun + " " + saat;
}

/**
 * "yaklaşık 2 sa sonra (~18:40), kaba tahmin" gibi bir cümle üretir; tahmin
 * yoksa boş döner. Sunucu mutlak bitiş saatini (tahmini_bitis_at) zaten
 * hesaplayıp gönderiyor — ekranın amacı "ne zaman bitecek"i göstermek
 * olduğundan göreli metnin yanına mutlak saat de eklenir. tahmini_bitis_at
 * null ise (sunucu henüz hesaplayamamışsa) eski davranış korunur: yalnız
 * göreli metin, güven 'kaba' ise sonuna eski "(kaba tahmin)" biçiminde.
 */
export function tahminMetni(detay: KosuDetay): string {
    const kalan = detay.tahmin.tahmini_kalan_saniye;
    if (kalan === null || kalan === undefined) {
        return "";
    }
    let metin = "yaklaşık " + sureMetni(kalan) + " sonra";
    const mutlakSaat = saatMetni(detay.tahmin.tahmini_bitis_at);
    if (!mutlakSaat) {
        if (detay.tahmin.tahmin_guveni === "kaba") {
            metin += " (kaba tahmin)";
        }
        return metin;
    }
    metin += " (~" + mutlakSaat + ")";
    if (detay.tahmin.tahmin_guveni === "kaba") {
        metin += ", kaba tahmin";
    }
    return metin;
}

/* ------------------------------------------------------------------ */
/* Ortam seçimi                                                         */
/* ------------------------------------------------------------------ */

/** Gece koşusu ekranının bağlanabileceği bir API. */
export interface GeceKosusuOrtami {
    anahtar: string;
    etiket: string;
    apiUrl: string; /* sonunda "/" ile */
}

export interface GeceKosusuOrtamListesi {
    ortamlar: GeceKosusuOrtami[];
    /** true: liste derleme ayarından geldi, seçici gösterilir. false: yalnız panelin kendi API'si. */
    yapilandirildi: boolean;
}

/** Liste ucunun üst düzeyde döndürdüğü yetenek özeti. Eski API'lerde bu alan yoktur. */
export interface KosuYetenekleri {
    manuel_tetikleme: boolean;
    tetikleme_modu: "surec" | "kuyruk";
    /* Durdur/Başlat, tenant arama ve ileri tarihli test alanları — daha yeni API'lerde vardır. */
    durdur_baslat?: boolean;
    tenant_arama?: boolean;
    ileri_tarih_test?: boolean;
    otomatik_devam_azami?: number;
}

/** GET /admin/gece-kosusu/panel/tenant/{tenantId} yanıtının data alanı (R5: tek büro arama). */
export interface TenantAramaSonucu {
    tenant_id: string;
    tenant_adi: string | null;
    lisans_var: boolean;
    db_var: boolean;
    uyari: TenantAtlanmaNedeni | null;
}

function sonuEgikCizgili(url: string): string {
    return url.endsWith("/") ? url : url + "/";
}

/**
 * Ortam listesi DERLEME ANINDA `VUE_APP_GECE_KOSUSU_ORTAMLARI` değişkeninden okunur:
 *   [{"anahtar":"cloudtest","etiket":"cloudtest","apiUrl":"https://apigecetest.zirve-bilgisayar.com/"}]
 *
 * NEDEN ORTAM DEĞİŞKENİ: aynı kod hem canlı panelde hem test2admin'de çalışır.
 * Değişken tanımsızsa (canlı panel) ekran yalnız panelin kendi API'sine bağlanır
 * ve seçici görünmez; test2admin derlemesinde değişken verilerek test API'leri
 * listelenir. Böylece dala özgü sabit bir liste koda gömülmez ve master'a alırken
 * taşınacak bir ortam bilgisi olmaz.
 *
 * Bozuk ya da eksik girdiler atlanır; geçerli hiç girdi kalmazsa varsayılana
 * düşülür. Yanlış bir derleme ayarı ekranı açılamaz hâle getirmesin.
 */
export function geceKosusuOrtamlari(varsayilanApiUrl: string): GeceKosusuOrtamListesi {
    const varsayilan: GeceKosusuOrtamListesi = {
        ortamlar: [{anahtar: "varsayilan", etiket: "Bu ortam", apiUrl: sonuEgikCizgili(varsayilanApiUrl || "")}],
        yapilandirildi: false
    };
    const ham = process.env.VUE_APP_GECE_KOSUSU_ORTAMLARI;
    if (!ham) {
        return varsayilan;
    }
    let dizi: any;
    try {
        dizi = JSON.parse(ham);
    } catch (e) {
        return varsayilan;
    }
    if (!Array.isArray(dizi)) {
        return varsayilan;
    }
    const gorulen: { [anahtar: string]: boolean } = {};
    const ortamlar: GeceKosusuOrtami[] = [];
    for (const g of dizi) {
        if (!g || typeof g.anahtar !== "string" || typeof g.etiket !== "string" || typeof g.apiUrl !== "string") {
            continue;
        }
        const anahtar = g.anahtar.trim();
        const apiUrl = g.apiUrl.trim();
        if (anahtar === "" || gorulen[anahtar] || !/^https?:\/\//.test(apiUrl)) {
            continue;
        }
        gorulen[anahtar] = true;
        ortamlar.push({anahtar: anahtar, etiket: g.etiket.trim() || anahtar, apiUrl: sonuEgikCizgili(apiUrl)});
    }
    return ortamlar.length > 0 ? {ortamlar: ortamlar, yapilandirildi: true} : varsayilan;
}

/** Anahtara göre ortamı bulur; bulunamazsa listedeki ilk ortam döner. */
export function ortamBul(liste: GeceKosusuOrtamListesi, anahtar: string | null | undefined): GeceKosusuOrtami {
    const bulunan = anahtar ? liste.ortamlar.find(o => o.anahtar === anahtar) : undefined;
    return bulunan || liste.ortamlar[0];
}
